import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  createContext,
} from 'react';
import { useRouter } from 'next/router';

import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import {
  Favorite, NewReleases, Star, LocalFireDepartment, DataLossPrevention, Apps, GradientWand,
} from '@tmap/mmm-style-guide/src/Icon';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Tabs } from '@tmap/mmm-style-guide/src/Tabs';
import { Tab } from '@tmap/mmm-style-guide/src/Tab';

import useCurrentUser from '../hooks/useCurrentUser';
import addObjectParamsToPath from '../lib/addObjectParamsToPath';

// [state, setState]
export const ResetTagFiltersContext = createContext([false, () => { }]);

const FilterBarContainer = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginLeft: theme.spacing(-3),
    width: 'calc(100% + 48px)',
  },
}));

const ColoredIconTab = styled(Tab, {
  shouldForwardProp: (p) => p !== 'color',
})(({ theme, color }) => ({
  '&.Mui-selected': {
    '.MuiSvgIcon-root': {
      color: typeof color === 'function' ? color(theme) : color,
    },
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    maskImage: 'linear-gradient(to right, transparent, #fff 8%, 96%, transparent)',
  },
  '.MuiTabs-indicator': {
    backgroundColor: theme.palette.grey[900],
  },
  '.MuiTab-root': {
    opacity: 1,
    textTransform: 'capitalize',
    fontWeight: 400,
    paddingBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.down(499)]: {
      minWidth: '26%',
    },
    [theme.breakpoints.between(500, 600)]: {
      minWidth: '21%',
    },
    '.MuiSvgIcon-root': {
      marginBottom: theme.spacing(2),
    },
    ':not(.Mui-selected)': {
      color: theme.palette.grey[700],
      path: {
        fill: theme.palette.grey[700],
      },
    },
  },
  '.MuiTab-root.Mui-selected': {
    fontWeight: 500,
  },
  '.MuiTabs-scrollButtons.Mui-disabled': {
    opacity: 0.3,
  },
  '.MuiTabs-flexContainer': {
    justifyContent: 'space-between',
  },
}));

function TagFilterBar(props) {
  const router = useRouter();
  const user = useCurrentUser();
  const {
    query,
    path,
  } = props;

  const [resetFiltersFlag, setResetFiltersFlag] = useContext(ResetTagFiltersContext);

  const [filterState, setFilterState] = useState({
    regionZone: query?.rz || '',
    regionType: query?.rt || '',
    offerType: query?.ot || '',
    tag: query?.tag || '',
  });

  const clearAll = () => {
    setFilterState((state) => Object.fromEntries(
      Object.keys(state).map((name) => [name, '']),
    ));
  };

  const handleTagChange = useCallback((e, newTag) => {
    setFilterState({
      ...filterState,
      tag: newTag,
    });
    const newQuery = {
      ...router.query,
      tag: newTag,
    };
    delete newQuery.page;
    if (!newQuery.tag) delete newQuery.tag;
    if (path) {
      window.location.href = addObjectParamsToPath(path, newQuery);
    } else {
      router.push(
        { query: newQuery },
        undefined,
        { scroll: false },
      );
    }
  }, [filterState, path, router]);

  useEffect(() => {
    if (resetFiltersFlag.tagFilterBar) {
      setResetFiltersFlag(false);
      clearAll();
      handleTagChange(null, '');
    }
  }, [handleTagChange, resetFiltersFlag.tagFilterBar, setResetFiltersFlag]);

  return (
    <FilterBarContainer container alignItems='center' spacing={1}>
      <Grid item xs={12} md order={{ xs: 3, md: 2 }} sx={{ minWidth: 0 }}>
        <StyledTabs
          value={query?.tag || ''}
          onChange={handleTagChange}
          textColor='inherit'
          indicatorColor='secondary'
          variant='scrollable'
        >
          <ColoredIconTab value='recommended' icon={<GradientWand colors={['primary.light', 'pink.dark']} />} label='For You' />
          <ColoredIconTab value='' icon={<Apps />} label='All Programs' color={(theme) => theme.palette.secondary.main} />
          <ColoredIconTab value='new' icon={<NewReleases />} label='New' color={(theme) => theme.palette.warning.light} />
          <ColoredIconTab value='featured' icon={<Star />} label='Featured' color={(theme) => theme.palette.purple.main} />
          <ColoredIconTab value='popular' icon={<LocalFireDepartment />} label='Popular' color={(theme) => theme.palette.convert.main} />
          <ColoredIconTab value='recruitingNow' icon={<DataLossPrevention />} label='Recruiting' color={(theme) => theme.palette.info.main} />
          {(user?._id || filterState.tag === 'favorite') && <ColoredIconTab value='favorite' icon={<Favorite />} label='My Favorited' color={(theme) => theme.palette.favorite.main} />}
        </StyledTabs>
      </Grid>
    </FilterBarContainer>
  );
}

export default TagFilterBar;
